import React, { useState, useEffect } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { ShieldCheck, User, Save, X, Loader2, CheckSquare, Square, Lock, Smartphone } from "lucide-react";

const AgentPermissionManager = () => {
  const [agentList, setAgentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  const allScreens = [
    { id: "DASHBOARD", label: "Dashboard" },
    { id: "PROJECTS", label: "Projects" },
    { id: "PLOTS", label: "Plots" },
    { id: "MEMBERS", label: "Members" },
    { id: "RECEIPTS", label: "Receipts" },
    { id: "HOLIDAYS", label: "Holidays" },
    { id: "DOCUMENTS", label: "Documents" },
    { id: "BRANCHES", label: "Branches" },
    { id: "DESIGNATIONS", label: "Designations" },
    { id: "LEADS", label: "Leads" },
    { id: "AGENTS", label: "Agents" },
    { id: "MEMBERREPORT", label: "MemberReport" },
    { id: "RECEIPTREPORT", label: "ReceiptReport" },
    { id: "COMPANYPROFILE", label: "Company Profile" },
    { id: "MANAGESCREENS", label: "Manage Screens" }

  ];

  const fetchAgentsWithAccess = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.AGENT_PERMISSION_GET_WITH_LOGIN}`);
      if (res.ok) {
        const data = await res.json();
        setAgentList(data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAgentsWithAccess(); }, []);

  const handleManageClick = async (agent) => {
    setSelectedAgent(agent);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.AGENT_PERMISSION_GET_BY_AGENT}/${agent.agentId}`);
      if (res.ok) {
        const currentPerms = await res.json();
        setSelectedPermissions(currentPerms);
      }
    } catch (e) { console.error(e); }
  };

  const togglePermission = (id) => {
    setSelectedPermissions(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.AGENT_PERMISSION_UPDATE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agentId: selectedAgent.agentId,
          screenNames: selectedPermissions
        })
      });
      if (res.ok) {
        Swal.fire({
            icon: 'success',
            title: 'Permissions Updated',
            text: `Agent ${selectedAgent.name} screens updated.`,
            confirmButtonColor: '#4f46e5'
        });
        setSelectedAgent(null);
        fetchAgentsWithAccess();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    card: { backgroundColor: "#fff", borderRadius: "24px", padding: "30px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", border: '1px solid #f1f5f9' },
    agentRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px", borderBottom: "1px solid #f8fafc", borderRadius: '16px', marginBottom: '10px', transition: '0.3s' },
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, backdropFilter: "blur(6px)" },
    modalContent: { backgroundColor: "#fff", borderRadius: "32px", width: "550px", padding: "40px", boxShadow: '0 25px 50px rgba(0,0,0,0.2)' },
    permItem: { display: "flex", alignItems: "center", gap: "12px", padding: "14px", borderRadius: "14px", cursor: "pointer", transition: "0.2s" },
    btnManage: { backgroundColor: "#eef2ff", color: "#4f46e5", border: "none", padding: "10px 20px", borderRadius: "12px", fontWeight: "800", cursor: "pointer", display: 'flex', alignItems: 'center', gap: '8px' }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "40px" }}>
          <div>
            <h1 style={{ fontSize: "32px", fontWeight: "900", color: "#1b2559", margin: 0 }}>Manage Screens</h1>
         
          </div>
          
        </div>

        <div style={styles.card}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}><Loader2 className="animate-spin" color="#4f46e5" size={40} /></div>
          ) : agentList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#a3aed0' }}>
              <Lock size={40} style={{ marginBottom: '15px' }} />
              <p style={{ fontWeight: '700' }}>No Agents found with "Login Access" enabled.</p>
            </div>
          ) : (
            agentList.map(agent => (
              <div key={agent.agentId} style={styles.agentRow}>
                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                  <div style={{ width: "50px", height: "50px", backgroundColor: "#f4f7fe", borderRadius: "15px", display: "flex", justifyContent: "center", alignItems: "center", color: "#4f46e5" }}>
                    <User size={24} />
                  </div>
                  <div>
                    <div style={{ fontWeight: "900", color: "#1b2559", fontSize: '17px' }}>{agent.name}</div>
                    <div style={{ display: 'flex', gap: '15px', color: "#a3aed0", fontSize: "13px", fontWeight: '700', marginTop: '3px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Lock size={14} /> {agent.code}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Smartphone size={14} /> {agent.phoneNo}</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => handleManageClick(agent)} style={styles.btnManage}>
                  Manage Screens <ShieldCheck size={18} />
                </button>
              </div>
            ))
          )}
        </div>

        {selectedAgent && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "35px" }}>
                <div>
                    <h3 style={{ margin: 0, fontWeight: "900", color: '#1b2559', fontSize: '22px' }}>Configure Access</h3>
                    <p style={{ margin: 0, color: '#a3aed0', fontWeight: '700', fontSize: '14px' }}>Agent: {selectedAgent.name}</p>
                </div>
                <div onClick={() => setSelectedAgent(null)} style={{ cursor: "pointer", color: "#a3aed0", padding: '5px', borderRadius: '50%', background: '#f8fafc' }}><X size={24} /></div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "35px", maxHeight: '400px', overflowY: 'auto', paddingRight: '5px' }}>
                {allScreens.map(screen => (
                  <div key={screen.id} onClick={() => togglePermission(screen.id)} 
                       style={{ 
                         ...styles.permItem, 
                         backgroundColor: selectedPermissions.includes(screen.id) ? "#f0fdf4" : "#f8fafc", 
                         border: selectedPermissions.includes(screen.id) ? "1.5px solid #10b981" : "1.5px solid #e2e8f0" 
                       }}>
                    {selectedPermissions.includes(screen.id) ? <CheckSquare size={20} color="#10b981" /> : <Square size={20} color="#cbd5e1" />}
                    <span style={{ fontSize: "14px", fontWeight: "800", color: selectedPermissions.includes(screen.id) ? "#065f46" : "#1b2559" }}>{screen.label}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <button onClick={() => setSelectedAgent(null)} style={{ flex: 1, backgroundColor: "#f4f7fe", color: "#a3aed0", border: "none", padding: "16px", borderRadius: "16px", fontWeight: "800", cursor: "pointer" }}>Close</button>
                <button onClick={handleSave} disabled={saving} style={{ flex: 2, backgroundColor: "#1b2559", color: "#fff", border: "none", padding: "16px", borderRadius: "16px", fontWeight: "800", cursor: "pointer", display: "flex", justifyContent: "center", alignItems: 'center', gap: "10px" }}>
                    {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />} {saving ? "Updating..." : "Save Permissions"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AgentPermissionManager;